<template>
    <div class="custom-highcharts-artifact">
        <chart-base
            ref="chartBase"
            :chart-options="highchartConfig" />
    </div>
</template>

<script>
import { PendoNotification } from '@pendo/components';
import ChartBase from '@/common/chart/chart-base.vue';
import { mapActions, mapGetters } from 'vuex';
import merge from 'lodash/merge';
import { downloadImage } from '@/dashboards-v2/widgets/utils/utils';
import Highcharts from 'highcharts';
import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode';

export default {
    name: 'CustomHighChartsArtifact',
    components: {
        ChartBase,
    },
    props: {
        artifactId: {
            type: String,
            default: null,
        },
        name: {
            type: String,
            default: 'Custom Chart',
        },
        highchart: {
            type: Object,
            default: null,
        },
        data: {
            type: Object,
            default: null,
        },
    },
    setup () {
        const { isPendoDarkMode } = useDarkMode();

        return {
            isPendoDarkMode,
        };
    },
    data () {
        return {
            isDownloadingPNG: false,
        };
    },
    computed: {
        ...mapGetters({
            getArtifactConfig: 'askPendo/getArtifactConfig',
        }),
        chartTypeOptions () {
            return [
                {
                    id: 'line',
                    label: this.$t('askPendo.customHighchartsArtifact.Line chart'),
                    icon: 'line-chart',
                    onClick: () => {
                        this.updateChartType('line');
                    },
                },
                {
                    id: 'column',
                    label: this.$t('askPendo.customHighchartsArtifact.Column chart'),
                    icon: 'bar-chart-big',
                    onClick: () => {
                        this.updateChartType('column');
                    },
                },
                {
                    id: 'bar',
                    label: this.$t('askPendo.customHighchartsArtifact.Bar chart'),
                    icon: 'bar-chart-horizontal-big',
                    onClick: () => {
                        this.updateChartType('bar');
                    },
                },
                {
                    id: 'area',
                    label: this.$t('askPendo.customHighchartsArtifact.Area chart'),
                    icon: 'area-chart',
                    onClick: () => {
                        this.updateChartType('area');
                    },
                },
                {
                    id: 'pie',
                    label: this.$t('askPendo.customHighchartsArtifact.Donut chart'),
                    icon: 'chart-donut',
                    onClick: () => {
                        this.updateChartType('pie');
                    },
                },
            ];
        },
        currentChartType () {
            if (this.highchart) {
                return this.highchart.chart?.type;
            }

            return this.data?.chart?.type;
        },
        highchartConfig () {
            if (this.highchart) {
                return merge({}, this.highchart, {
                    chart: {
                        type: this.savedChartType || this.currentChartType,
                    },
                });
            }

            const highlightedDataConfig = this.handlePointHighlights(this.data);

            const finalChartOptions = merge({}, highlightedDataConfig, {
                chart: {
                    type: this.savedChartType || this.currentChartType,
                },
                plotOptions: {
                    series: {
                        animation: {
                            defer: 400,
                            duration: 200,
                        },
                    },
                },
            });

            return finalChartOptions;
        },
        savedChartType () {
            return this.getArtifactConfig(this.artifactId)?.chartType;
        },
    },
    beforeUnmount () {
        this.clearArtifactMenuItems(this.artifactId);
    },
    mounted () {
        this.setArtifactMenuItems({
            artifactId: this.artifactId,
            items: [
                {
                    id: 'chart-type',
                    label: 'Chart type',
                    icon: 'line-chart',
                    items: this.chartTypeOptions,
                },
                {
                    id: 'copy-png',
                    icon: 'image',
                    label: this.$t('askPendo.Copy PNG'),
                    onClick: () => {
                        this.copyArtifactImage();
                    },
                },
                {
                    id: 'download-png',
                    icon: 'download',
                    label: this.$t('askPendo.Download PNG'),
                    onClick: () => {
                        this.downloadArtifactImage();
                    },
                },
            ],
        });
    },
    methods: {
        ...mapActions('askPendo', ['clearArtifactMenuItems', 'setArtifactConfig', 'setArtifactMenuItems']),
        updateChartType (chartType) {
            if (this.artifactId) {
                this.setArtifactConfig({
                    artifactId: this.artifactId,
                    config: {
                        chartType,
                    },
                });
            }
        },
        hasHighlights (series) {
            return series.some((individualSeries) =>
                individualSeries.data.some((point) => point.highlight),
            );
        },
        handlePointHighlights (config) {
            if (!config?.series || !config?.colors) {
                return config;
            }

            const { series, colors } = config;

            if (!this.hasHighlights(series)) {
                return config;
            }

            const highlightedSeries = series.map((individualSeries, seriesIndex) => {
                const baseColor = colors?.[seriesIndex] || colors?.[0];

                // eslint-disable-next-line
                const colorTo = this.isPendoDarkMode ? "black" : "white";
                const desaturatedColor = Highcharts.color(baseColor).tweenTo(
                    Highcharts.color(colorTo),
                    0.5,
                );

                return {
                    ...individualSeries,
                    data: individualSeries.data.map((point) => ({
                        ...point,
                        color: point.highlight ? baseColor : desaturatedColor,
                        marker: {
                            enabled: !!point.highlight,
                        },
                        dataLabels: {
                            enabled: !!point.highlight,
                            style: {
                                color: 'var(--palette-gray-100)',
                                fontWeight: 'bold',
                                textOutline: 'none',
                            },
                        },
                    })),
                };
            });

            return { ...config, series: highlightedSeries };
        },
        getChartElement () {
            return this.$refs.chartBase?.$el;
        },
        async copyArtifactImage () {
            try {
                const chartElement = this.getChartElement();

                if (chartElement) {
                    const filename = `${this.name || 'chart'}.png`;
                    await downloadImage(chartElement, filename, { title: this.name }, 'copy');
                    PendoNotification({
                        type: 'success',
                        title: this.$t('askPendo.Chart copied'),
                        message: this.$t('askPendo.The PNG of the chart has been copied to your clipboard'),
                        duration: 5000,
                    });
                }
            } catch {
                PendoNotification({
                    type: 'error',
                    title: this.$t('askPendo.Failed to copy chart'),
                    message: this.$t('askPendo.Something went wrong. Please try again later.'),
                    duration: 5000,
                });
            }
        },
        async downloadArtifactImage () {
            this.isDownloadingPNG = true;

            try {
                const chartElement = this.getChartElement();

                if (chartElement) {
                    const filename = `${this.name || 'chart'}.png`;
                    await downloadImage(chartElement, filename, { title: this.name });
                }
            } catch {
                PendoNotification({
                    type: 'error',
                    title: this.$t('askPendo.Failed to download chart'),
                    message: this.$t('askPendo.Something went wrong. Please try again later.'),
                    duration: 5000,
                });
            } finally {
                this.isDownloadingPNG = false;
            }
        },
    },
};
</script>

<style scoped lang="less">
.custom-highcharts-artifact {
    padding: 8px;
}
.artifact-chart-type-selector {
    margin-left: auto;
    min-width: 160px;
    border-radius: 3px;
    border: 1px solid var(--color-gray-40);
}

.header-action {
    margin: 0;
}

:deep(.pendo-card__header--left) {
    line-height: 150%;
}
</style>
